import React, { useState, useEffect } from "react";

import Box from '@mui/material/Box';

import "../App.css"
import {PageHeader} from "./PageHeader";
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {accessCtrl} from "../ctx/AccessCtrl";
import {CxDataGrid} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {
    CxResetSearchButton,
    CxComboxSelection,
    CxDatePicker,
    CxTextField,
    CxRadioboxSelection,
    CxAutocomplete,
    CxLocalization
} from "../cmx/CxForm";
import {CxFormFeedback, CxSnack} from "../cmx/CxFeedback";
import {CxSubtitle, CxText} from "../cmx/CxTypography"
import {CxReportDialog} from "../cmx/CxReport";
import {taskSearchList} from "../gateway/TaskService"
import {Task} from "../domain/Task";
import {Report} from "../domain/Report";
import {initialCall, runningCall, successCall} from "../gateway/CallService";
import {DateUtil} from "../lib/DateUtil";
import {ValidationUtil} from "../lib/ValidationUtil";

function Search({type}) {

    const {handleAction, getPageData, setPageData, getPageState, setPageState} = useAppCtx();
    const {crew, lazyloadCrews} = useCrewCtx();
    const {code, lazyloadCodes} = useCodeCtx()

    const pageId = 'TaskSearch';
    const viewKey = 'view';

    useEffect(() => {
        lazyloadCrews();
        lazyloadCodes();
    }, []);

    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const typeCfg = {
        task_currentView: {
            title       : "Arbeitsvorgänge suchen",
            reportTitle : "Arbeitsvorgänge",
            desc        : "Geben Sie die Suchkriterien ein die alle gemeinsam zutreffen sollen. Beachte: die Suche ist auf max. 100 Treffer limitiert.",
            dataKey     : "taskSearchCriteria",
            returnTo    : "taskSearch",
            isTask      : true,
            isCurrent   : true,
            isWarranty  : false
        },
        task_archiveView: {
            title       : "Archivierte Arbeitsvorgänge suchen",
            reportTitle : "Archivierte Arbeitsvorgänge",
            desc        : "Geben Sie die Suchkriterien ein die alle gemeinsam zutreffen sollen. Beachte: die Suche ist auf max. 100 Treffer limitiert.",
            dataKey     : "taskSearchCriteria",
            returnTo    : "taskSearch",
            isTask      : true,
            isCurrent   : false,
            isWarranty  : false
        },
        warranty: {
            title       : "Garantiefälle suchen",
            reportTitle : "Garantiefälle",
            desc        : "Geben Sie die Suchkriterien ein die alle gemeinsam zutreffen sollen. Beachte: die Suche ist auf max. 100 Treffer limitiert.",
            dataKey     : "warrantySearchCriteria",
            returnTo    : "taskWarrantySearch",
            isTask      : false,
            isCurrent   : true,
            isWarranty  : true
        }
    };

    const view = getPageState(pageId, viewKey);
    const cfgKey = ['warranty'].includes(type) ? type : type + "_" + view;
    const cfg = typeCfg[cfgKey];

    const handleViewChange = (value) => {
        setPageState(pageId, viewKey, value);
        setCall(initialCall());
    };

    const viewItems = [
        { label: "aktuelle",    value: "currentView" },
        { label: "archivierte", value: "archiveView" }
    ];

    const localActionItems = [
        { kind: "tooltip", label: "Report erstellen", iconName: "report", actionName: "openReport" }
    ];

    const handleLocalActionChange = (value) => {
        if (value === "openReport") {
            setReport(true);
        }
    };

    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Planung", iconName: "plan", actionName: "task" }
    ];

    const renderHeader = () => {
        return (
            <>
                { cfg.isTask &&
                    <PageHeader
                        title={cfg.title}
                        viewItems={viewItems}
                        viewInitialValue={view}
                        viewOnChangeHandler={handleViewChange}
                        // localActionItems={localActionItems}
                        // localActionOnChangeHandler={handleLocalActionChange}
                        actionItems={actionItems}
                    />
                }

                { ! cfg.isTask &&
                    <PageHeader
                        title={cfg.title}
                        localActionItems={localActionItems}
                        localActionOnChangeHandler={handleLocalActionChange}
                        actionItems={actionItems}
                    />
                }

                <CxGrid size="xl">
                    <CxText>{cfg.desc}</CxText>
                </CxGrid>
            </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const dataKey = cfg.dataKey

    const [formValues, setFormValues] = useState( getPageData(pageId, dataKey, Task.getSearchValues()) );
    const [formErrors, setFormErrors] = useState( Task.getSearchErrors() );
    const [call, setCall] = useState(initialCall());
    const [snack, setSnack] = useState(null);
    const [report, setReport] = useState(false);
    const [sortModel, setSortModel] = useState([])

    //
    // handers
    //
    const handleChange = (fieldOrEvent, value) => {
        if (typeof fieldOrEvent === "string") {
            // For DatePicker
            setFormValues((prev) => ({ ...prev, [fieldOrEvent]: DateUtil.convertDateToIsoString(value) }));
            setFormErrors((prev) => ({ ...prev, [fieldOrEvent]: false }));
        } else {
            // For TextField or CheckBox
            const { name, value, type, checked} = fieldOrEvent.target;
            if (type === "checkbox") {
                // For CheckBox: Convert checked state to 0/1
                setFormValues((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
            } else {
                // For TextField: Sanitize and update value
                setFormValues((prev) => ({ ...prev, [name]: value }));
            }
            setFormErrors((prev) => ({ ...prev, [name]: false }));
        }
    };

    const handleComboxChange = (field, value) => {

        // set task_code_key and dependend fields
        if (field === "task_code_key") {
            setFormValues((prevValues) => ({
                ...prevValues,
                task_code_key: value,
                sub_task_code_key: -1,  // reset sub task selection
            }));
        } else {
            setFormValues((prevValues) => ({
                ...prevValues,
                [field]: value
            }));
        }
    }

    const handleTeamLeadSourceChange = (value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            team_lead_source: value,
            team_lead_crew_id: null,
            team_member_crew_ids: []
        }));
        formErrors.team_lead_crew_id = "";
    }

    const handleTeamLeadChange = (event, item) => {
        // set value or reset if item is null
        let teamLeadCrewId = null;
        if (item) {
            teamLeadCrewId = item.value;
        }
        setFormValues((prevValues) => ({
            ...prevValues,
            team_lead_crew_id: teamLeadCrewId,
        }));

        // reset error
        setFormErrors((prev) => ({ ...prev, team_lead_crew_id: false }));
    };

    const handleClearedSourceChange = (value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            cleared: value,
        }));
        formErrors.cleared = "";
    }

    const handleWarrantyChange = (event, item) => {
        // set value or reset if item is null
        let warrantyCrewId = null;
        if (item) {
            warrantyCrewId = item.value;
        }
        setFormValues((prevValues) => ({
            ...prevValues,
            warranty_crew_id: warrantyCrewId
        }));

        // reset error
        setFormErrors((prev) => ({ ...prev, warranty_crew_id: false }));
    };

    const handleSubmit = async (action) => {

        if (action === "reset") {
            setPageData(pageId, dataKey, Task.getSearchValues() );
            setFormValues( Task.getSearchValues() )
            setFormErrors( Task.getSearchErrors() )
            setCall(initialCall());
            return
        }

        if (action === "search") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);
            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save search data
            setPageData(pageId, dataKey, formValues);
            // execute search
            search( Task.mapSearchToQuery(formValues) );
        }
    };

    //
    // validation
    //
    function validate(formValues, formErrors) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues)

        // check if all search fields are empty
        if ( ValidationUtil.areEqual(formValues, Task.getSearchValues()) ) {
            const snack= `Bitte mindestens 1 Suchkriterium eingeben.`;
            setSnack(snack)
            return false;
        }

        // validate minimal lenth
        isValid = ValidationUtil.minLength(formValues, formErrors, Task.getSearchMinimalLength());

        // validate date range
        if (! ValidationUtil.dateRange(formValues, formErrors, 'start_date', 'end_date') ){
            isValid = false;
        }

        // return final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formTextField = (field, label, maxLength) => {
        return <CxTextField field={field} label={label} maxLength={maxLength}
                            handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formDatePicker = (field, label) => {
        return <CxDatePicker field={field} label={label} disablePast={false}
                             handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items) => {
        return <CxComboxSelection
            key={formValues[field]}     // force rerender on reset
            id={field}
            name={label}
            value={formValues[field]}
            onChange={(value) => handleComboxChange(field, value)}
            items={items}
            formErrors={formErrors} />
    }

    const formTeamLeadSource = () => {
        return <CxRadioboxSelection
            key={formValues['team_lead_source']}
            name='team_lead_source'
            label="Einsatz"
            value={formValues['team_lead_source']}
            items={crew.getTeamLeadSourceItems(true)}
            onChange={handleTeamLeadSourceChange}  />
    }

    const formTeamLeadSelection = () => {
        let source = formValues['team_lead_source'];
        let items = crew.getTeamLeadItems(source);
        let current_id = formValues['team_lead_crew_id']
        let currentValue = items.find(item => item.value === current_id) || null;

        return <CxAutocomplete key={source}
                               label={crew.getTeamLeadSourceLabel(source)}
                               currentValue={currentValue}
                               items={items}
                               onChange={handleTeamLeadChange}
                               error={formErrors['team_lead_crew_id']} />
    }

    const formClearedSelection = () => {
        return <CxRadioboxSelection
            key={formValues['cleared']}
            name='cleared'
            label="Abgeräumt"
            value={formValues['cleared']}
            items={Task.getSearchClearedItems(true)}
            onChange={handleClearedSourceChange}  />
    }

    const formWarrantySelection = (readOnly) => {
        let items = crew.getWarrantySelection();
        let current_id = formValues['warranty_crew_id']
        let currentValue = items.find(item => item.value === current_id) || null;

        return <CxAutocomplete key="warranty_selection"
                               label="Garantie Verursacher"
                               currentValue={currentValue}
                               items={items}
                               readOnly={readOnly}
                               onChange={handleWarrantyChange}
                               error={formErrors['warranty_crew_id']} />
    }

    const formResetSearchButton = () => {
        return <CxResetSearchButton handleSubmit={handleSubmit} />
    }

    const renderCtrlTask = () => {
        return (
            <CxLocalization>

                <CxGrid size="lg">{ formCombox("task_code_key", "Arbeitskategorie",
                    code.getTaskSelection("Alle")) }</CxGrid>
                <CxGrid size="lg">{ formCombox("sub_task_code_key", "Arbeitsvorgang",
                    code.getSubTaskSelection(formValues["task_code_key"], "Alle")) }</CxGrid>

                <CxGrid size="lg">{ formDatePicker("start_date", "Startdatum ab")}</CxGrid>
                <CxGrid size="lg">{ formDatePicker("end_date", "Enddatum bis")}</CxGrid>

                <CxGrid size="lg">{ formTeamLeadSource() }</CxGrid>
                <CxGrid size="lg">{ formTeamLeadSelection() }</CxGrid>

                <CxGrid size="lg">{ formTextField("dimension", "Mass", 64) }</CxGrid>
                <CxGrid size="lg">{ formTextField("info", "Information", 64) }</CxGrid>

                <CxGrid size="lg">{ formClearedSelection() }</CxGrid>
                <CxGrid size="lg" />

                <CxGrid size="xl" justifyContent="flex-end">{ formResetSearchButton() }</CxGrid>
                <CxSnack message={snack} handleClose={() => setSnack(null)} position="topCenter" />

            </CxLocalization>
        );
    }

    const renderCtrlWarranty = () => {
        return (
            <CxLocalization>

                <CxGrid size="lg">{ formDatePicker("start_date", "Startdatum ab")}</CxGrid>
                <CxGrid size="lg">{ formDatePicker("end_date", "Enddatum bis")}</CxGrid>

                <CxGrid size="lg">{ formWarrantySelection() }</CxGrid>
                <CxGrid size="lg">{ formTextField("warranty_cause", "Garantie Ursache/Bemerkung", 255) }</CxGrid>

                <CxGrid size="xl" justifyContent="flex-end">{ formResetSearchButton() }</CxGrid>
                <CxSnack message={snack} handleClose={() => setSnack(null)} position="topCenter" />

            </CxLocalization>
        );
    }

    const renderCtrl = () => {
        return (
            <>
                { cfg.isTask     && renderCtrlTask() }
                { cfg.isWarranty && renderCtrlWarranty() }
            </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // select view data
    // ----------------------------------------------------------------------------------------------------------------

    function search(queryParams) {
        setCall(runningCall())
        taskSearchList(type, queryParams, cfg.isCurrent).then(responseCall => {
            if (responseCall.isSuccess) {

                const mappedRows = Task.mapListColumns(responseCall.result, code, crew);
                setCall(successCall(mappedRows));

            } else {
                setCall(responseCall);
            }
        });
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render report
    // ----------------------------------------------------------------------------------------------------------------

    const reportInfo = () => {
        const parts = [];

        if (formValues.start_date)             parts.push(`Startdatum ab: ${DateUtil.convertIsoStringToString(formValues.start_date)}`);
        if (formValues.end_date)               parts.push(`Enddatum bis: ${DateUtil.convertIsoStringToString(formValues.end_date)}`);
        if (formValues.warranty_crew_id &&
            formValues.warranty_crew_id != -1) parts.push(`Garantie Verursacher: ${crew.getName(formValues.warranty_crew_id)}`);
        if (formValues.warranty_cause)         parts.push(`Garantie Ursache: ${formValues.warranty_cause}`);

        return parts.length > 0 ? parts.join(', ') : null;
    }

    const reportSortedRows = () => {
        const rows = call.isSuccess ? [...call.result] : [];
        return Report.sortByGridSortModel(rows, sortModel);
    }

    const renderReport = () => {
        return (
            <>
                { cfg.isWarranty &&
                    <CxReportDialog
                        reportType={Report.WARRANTY}
                        title={cfg.reportTitle}
                        rows={reportSortedRows()}
                        open={report}
                        onClose={() => setReport(false)}
                        info={reportInfo()}
                    />
                }
            </>
        )
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleRowDoubleClick = (row) => {
        if (cfg.isCurrent) {
            handleAction("projectDetailFromTask", row, cfg.returnTo);
        } else {
            handleAction("archiveDetailFromTask", row);
        }
    };

    const handleEditClick = (row) => {
        handleAction("taskUpdate", row, cfg.returnTo);       // return back to 'project' view after edit
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>

                { renderHeader() }
                { renderCtrl() }

                <CxFormFeedback call={call} />

                { call.isSuccess &&
                    <>
                        <CxSubtitle>Resultate</CxSubtitle>

                        <CxGrid size="xl">
                            <CxDataGrid
                                rows={call.result}
                                columns={Task.getListColumns(false, cfg.isWarranty)}
                                onRowDoubleClick={(params) => handleRowDoubleClick(params.row)}
                                onViewAction={(params) => handleRowDoubleClick(params.row)}
                                onEditAction={cfg.isCurrent && accessCtrl.canManageAllTask ? (params) => handleEditClick(params.row) : undefined}
                                onSortModelChange={(model) => setSortModel(model)}
                                noRecordsMessage="Keine Suchresultate gefunden."
                            />
                        </CxGrid>
                    </>
                }

                { report && renderReport() }

            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// External TaskSearch Components
// --------------------------------------------------------------------------------------------------------------------

export function TaskSearch() {
    return <Search type="task" />
}

export function TaskWarrantySearch() {
    return <Search type="warranty" />
}

// --------------------------------------------------------------------------------------------------------------------
// The end.